import * as React from 'react'

import { SidebarMenuButton, useSidebar } from '@/components/sui/sidebar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/sui/tooltip'
import { cn } from '@/lib/utils'

type SidebarMenuButtonProps = React.ComponentProps<typeof SidebarMenuButton>

interface SidebarMenuButtonWithTooltipProps extends Omit<
  SidebarMenuButtonProps,
  'tooltip'
> {
  tooltip: React.ReactNode
}

/**
 * Wrapper around SidebarMenuButton that correctly handles tooltips when using
 * custom render props (like Link components).
 *
 * The original SidebarMenuButton has a bug where tooltips don't position correctly
 * when a render prop is passed, and nested buttons when no render prop is passed.
 * This wrapper fixes both issues by always managing the Tooltip externally and
 * passing the render prop to TooltipTrigger.
 */
export function SidebarMenuButtonWithTooltip({
  tooltip,
  render,
  children,
  className,
  ...props
}: SidebarMenuButtonWithTooltipProps) {
  const { isMobile, state } = useSidebar()

  // Determine what element the TooltipTrigger should render as
  // Clone the render element to add w-full class for proper width
  const triggerElement = render ? (
    React.cloneElement(render as React.ReactElement<{ className?: string }>, {
      className:
        `cursor-pointer w-full ${(render as React.ReactElement<{ className?: string }>).props.className || ''}`.trim(),
    })
  ) : (
    <button type="button" className="w-full" />
  )

  return (
    <Tooltip>
      <TooltipTrigger render={triggerElement}>
        <SidebarMenuButton
          render={<span className="contents" />}
          {...props}
          // Selected item reads by colour, not weight: faint primary wash +
          // deep gold text (brand yellow in dark), overriding the upstream
          // sidebar-accent/font-medium active treatment.
          className={cn(
            'data-active:bg-primary/10 data-active:text-nav-active data-active:font-normal',
            className,
          )}
        >
          {children}
        </SidebarMenuButton>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== 'collapsed' || isMobile}
      >
        {tooltip}
      </TooltipContent>
    </Tooltip>
  )
}
